// src/utils/resolveAssets.ts
// Central helpers to resolve media asset URLs from short names in markdown.
// Authors can write `video-h: clip.mp4` or `background: hero.jpg` and we map
// to `${baseUrl}/videos/...` or `${baseUrl}/images/...` unless an absolute/remote URL is given.

type Dir = "videos" | "images" | "audio";

const DIRS: Record<Dir, Dir> = {
  videos: "videos",
  images: "images",
  audio: "audio",
};

function getGlobalBase(dir: Dir, fallback?: string): string {
  const cleanFallback = (fallback || "").replace(/\/$/, "");
  if (typeof window === "undefined") return cleanFallback;
  const key =
    dir === "videos"
      ? "STORYCORE_VIDEO_BASE"
      : dir === "images"
      ? "STORYCORE_IMAGE_BASE"
      : "STORYCORE_AUDIO_BASE";
  const val = (window as any)?.[key];
  if (typeof val === "string" && val.trim().length > 0) {
    return val.replace(/\/$/, "");
  }
  return cleanFallback;
}

function trimSlash(s: string): string {
  return s.replace(/\/+$/g, "");
}
function ltrimSlash(s: string): string {
  return s.replace(/^\/+/g, "");
}

/** Accept http(s), protocol-relative, data:, blob:, and absolute filesystem-like paths we should skip. */
export function isAbsoluteOrRemote(url: string): boolean {
  if (!url) return false;
  const s = url.trim();
  return (
    /^(https?:)?\/\//i.test(s) ||        // http:, https:, protocol-relative //
    /^data:/i.test(s) ||                 // data:…
    /^blob:/i.test(s) ||                 // blob:…
    s.startsWith("/")                    // root-absolute (let the app/server handle)
  );
}

/** Safe join preserving query/hash; encodes only the path segment (not ?query or #hash). */
function joinUrl(base: string, dir: Dir, name: string): string {
  const baseClean = trimSlash(base);
  // Split `name` into path + search/hash
  const m = name.match(/^([^?#]*)(\?[^#]*)?(#.*)?$/) || [];
  const path = m[1] ?? "";
  const search = m[2] ?? "";
  const hash = m[3] ?? "";

  // Clean path (strip leading slashes that would escape dir)
  const pathClean = ltrimSlash(path.replace(/^\.\//, ""));

  // Encode each path segment individually (keep / separators)
  const encodedPath = pathClean
    .replace(/\\/g, "/")
    .split("/")
    .filter(Boolean)
    .filter(seg => seg !== "." && seg !== "..") // block traversal
    .map(decodeURIComponentSafe)
    .map(encodeURIComponent)
    .join("/");

  const url = `${baseClean}/${DIRS[dir]}/${encodedPath}${search}${hash}`;
  return url;
}

function decodeURIComponentSafe(s: string): string {
  try { return decodeURIComponent(s); } catch { return s; }
}

/**
 * Resolve an asset, returning a fully qualified URL string or null if name is empty.
 * - Leaves absolute/remote/data/blob/root-absolute URLs untouched.
 * - Joins `${baseUrl}/${dir}/${name}` with safe encoding otherwise.
 */
export function resolveAssetSrc(baseUrl: string, dir: Dir, name: string | null | undefined): string | null {
  if (!name) {
    console.warn("[resolveAssetSrc] Empty asset name provided", { dir, baseUrl });
    return null;
  }
  const raw = String(name).trim();
  if (!raw) {
    console.warn("[resolveAssetSrc] Blank asset name provided", { dir, baseUrl });
    return null;
  }
  if (isAbsoluteOrRemote(raw)) {
    // For root-absolute, protocol-relative, http(s), data:, blob:, return as-is
    return raw;
  }
  const resolved = joinUrl(baseUrl, dir, raw);
  // console.debug("[resolveAssetSrc] Resolved asset URL", { dir, name: raw, resolved });
  return resolved;
}

export function resolveVideoSrc(baseUrl: string, name: string | null | undefined): string | null {
  const base = getGlobalBase("videos", baseUrl);
  return resolveAssetSrc(base, "videos", name);
}

export function resolveImageSrc(baseUrl: string, name: string | null | undefined): string | null {
  const base = getGlobalBase("images", baseUrl);
  return resolveAssetSrc(base, "images", name);
}

export function resolveAudioSrc(baseUrl: string, name: string | null | undefined): string | null {
  const base = getGlobalBase("audio", baseUrl);
  return resolveAssetSrc(base, "audio", name);
}

export type AssetKind = "image" | "video" | "audio";

export function resolveAsset(
  kind: AssetKind,
  baseUrl: string,
  name: string | null | undefined
): string | null {
  switch (kind) {
    case "image": return resolveImageSrc(baseUrl, name);
    case "video": return resolveVideoSrc(baseUrl, name);
    case "audio": return resolveAudioSrc(baseUrl, name);
    default: return null;
  }
}
